import * as z from 'zod/mini';

// 1ª instrução executável (§2.6.2; FE §11.3): o zod v4 compilaria validadores com new Function, que a CSP bloqueia.
// Os imports abaixo são içados, mas nenhum deles valida com zod no topo do módulo.
z.config({ jitless: true });

import './styles/fonts.css';
import './styles/tokens.css';
import './styles/tokens.parties.css';
import './styles/base.css';
import './styles/modes.css';
import { batch } from '@preact/signals';
import { render } from 'preact';
import { App } from './app/App';
import { createSource, PRODUCTION_BUILD } from './app/createSource';
import { candidatesContext, FeedRuntime } from './app/feedBinding';
import { afterSkeletonPaint, deferUntilFirstPaint, firstSnapshotOrCap } from './app/firstPaint';
import { keepSessionAlive, learnOriginClock, siteAccess } from './app/siteGate';
import { keepBareHash, seedAppOpen } from './app/sitePhase';
import { installStaleBuildRecovery } from './app/staleBuild';
import { bootLayout } from './app/useLayoutMode';
import { resolveConfig } from './config';
import { holdBareHashWhile, initRoute, routerPort } from './router/navigate';
import { parseRoute } from './router/parseRoute';
import { bindRouter } from './state/actions';
import { clearAuthSession, readAuthSession } from './state/storage';
import { config, ctx, statics } from './state/store';

/** Únicas globais do app (§2.2.2 regra 5): o aperto de mão com a BootGuard inline (§12.7). */
interface BootWindow {
  __bootFail?: (err: unknown) => void;
}

/**
 * Montagem (§2.2.3 passo 2; FE §8.2): configuração → `layout` inicial com a MESMA função do esboço (M27) → rota
 * (`initRoute`, 1ª escrita de `route`, canoniza com replaceState) → `createSource` (§14.8.1) → estáticos mínimos
 * (`candidates.json`, caminho crítico de §2.2.4: o esboço continua até ele chegar; novas tentativas em 2, 4 e 8 s e a
 * guarda mostra a demora aos 30 s) → troca do esboço pelo app na mesma tarefa. O `FeedClient` parte antes do 1º render
 * (`runtime.start()`), e o app monta com o 1º snapshot aceito, com teto (DECISOES §40); o App só o para na desmontagem.
 */
async function boot(): Promise<void> {
  const root = document.getElementById('app');
  // A guarda reprovou o navegador (§12.7): o app nem tenta montar.
  if (!root || document.documentElement.getAttribute('data-boot') === 'unsupported') return;
  let runtime: FeedRuntime | null = null;
  try {
    const cfg = resolveConfig();
    config.value = cfg;
    // pré-mortem (ORD-F-146): um pedaço que sumiu depois de deploy ou de volta recarrega a página com o build novo
    installStaleBuildRecovery(cfg.build);
    ctx.value = { ...ctx.value, flags: cfg.features };
    // Sem a porta (o login liberado, §8 de 26/10 ~11h45; DECISOES §86): a sessão que ficou no aparelho de quem entrou
    // até 25/10 (o e-mail e o refresh token, `mapavoto.sessao`) não serve para nada aqui e é apagada já, antes de montar.
    if (!cfg.features.login_gate) clearAuthSession();
    // A porta (ORD-F-153; app/siteGate.ts, DECISOES §84), decidida aqui, antes de montar e antes de qualquer consulta ao
    // feed, só pelo aparelho: `public` não cria o FeedClient (e a pré-busca inline já não saiu, pela mesma regra).
    const access = siteAccess(cfg.features, readAuthSession() !== null);
    // `tv=1` na entrada (§5.2.7): o esboço já desenhou a TV; no celular a função de modo a ignora (AC-4.1-04).
    const tv = access !== 'public' && cfg.features.tv && parseRoute(location.hash, ctx.value, null).route.tvOnEntry;
    // a caixa da faixa do banner (§12.5 bar) entra na conta desde o boot: a mesma regra do slot (app/partnerSlot.tsx)
    bootLayout({ simulation: cfg.mode === 'simulation', partnerBar: cfg.features.partner_slots && !tv, tv });
    bindRouter(routerPort);
    // 26/10: um endereço de rota na entrada abre a apuração direto na fase `after` (com acesso ao app); enquanto a
    // abertura estiver na tela, o endereço sem rota fica sem rota (recarregar a abertura mostra a abertura)
    if (access !== 'public') seedAppOpen(location.hash);
    holdBareHashWhile(keepBareHash);
    initRoute(location.hash);
    const query = new URLSearchParams(location.search);
    const source = await createSource(cfg, query);
    const st = await source.statics();
    batch(() => {
      statics.value = { ...statics.value, candidates: st.candidates };
      ctx.value = { ...ctx.value, ...candidatesContext(st) };
    });
    if (access === 'public') {
      // a página principal pública: o relógio do aparelho, corrigido pelo `Date` de um HEAD à própria origem (sem feed)
      void learnOriginClock();
      await afterSkeletonPaint(root);
      root.replaceChildren();
      deferUntilFirstPaint();
      render(<App query={query} access="public" />, root);
      root.removeAttribute('aria-busy');
      return;
    }
    // com sessão: a renovação do token em segundo plano (falha de rede não desloga)
    if (access === 'session') keepSessionAlive();
    runtime = new FeedRuntime({ source, statics: st, config: cfg, production: PRODUCTION_BUILD, createSource });
    // O esboço (ou a nota da guarda) sai e o app entra na mesma tarefa, sem quadro intermediário: o render e os
    // efeitos de layout do AppShell (modo, palco, zoom) são síncronos, e o cabeçalho reaparece no mesmo lugar
    // (AC-17.2-10, AC-12.7-01).
    // o esboço é pintado antes de o app montar (DECISOES §38); o feed liga já, e o app monta com o 1º snapshot aceito
    // (teto de 800 ms), na ordem final das disputas (DECISOES §40). O App chama start() de novo (idempotente).
    runtime.start();
    await afterSkeletonPaint(root);
    await firstSnapshotOrCap();
    root.replaceChildren();
    // os painéis fora da 1ª tela montam depois da 1ª pintura (DECISOES §38)
    deferUntilFirstPaint();
    render(<App runtime={runtime} query={query} access={access} />, root);
    root.removeAttribute('aria-busy');
  } catch (err) {
    // Exceção na montagem ou cadastro inválido (§12.7): a guarda mostra boot.failed; depois do boot, os erros vão às
    // boundaries.
    // o feed já pode estar ligado (DECISOES §40): sem App montado, ninguém o pararia atrás da tela de falha
    runtime?.stop();
    const fail = (window as BootWindow).__bootFail;
    if (fail) fail(err);
    else console.error('[app] montagem:', err);
  }
}

void boot();
